import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { LoadingWrapper, NoResultsPlaceholder, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { Range } from 'react-date-range'

import 'chartjs-adapter-moment'

import BarChart, { BarChartBaseProps } from './BarChart'
import { EmptyGraph, TooltipHeaderItem } from './ChartsConfig'
import ChartLegend from './EmissionsChartLegend'

// Make sure that the data being consumed is all of the same type (data, dataColours, dataLabels and labelOrdering).
type ValidDatasetKey<T> = T extends string ? T : never

type EmissionChartSectionProps<T> = React.PropsWithChildren<
    {
        title?: string
        data: ({ date: string } & { [key in ValidDatasetKey<T>]: number })[]
        dataColours: { [key in ValidDatasetKey<T>]: string }
        dateRange: Range
        loading: boolean
        showLegend: boolean
        isEmpty?: boolean
        yAxisLabel?: string
        getTooltipHeaderItems?: (date: string) => TooltipHeaderItem[]
        chartHeight?: number
    } & BarChartBaseProps<T>
>

export const EmissionsChartSection = <T,>({
    title,
    data: unsortedData,
    dataColours,
    dataLabels,
    dateRange,
    loading,
    showLegend,
    labelOrdering,
    skipZeroOnTooltip,
    isEmpty,
    reverseTooltipOrder,
    yAxisLabel = 'tCO₂e',
    chartHeight,
    getTooltipHeaderItems,
}: EmissionChartSectionProps<T>) => {
    useLingui()

    const [graphLegend, setGraphLegend] = useState<{ color: string; label: string }[]>()
    const data = unsortedData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    const chartData = useMemo(() => {
        const rangeDuration = moment.duration(moment(dateRange.endDate).diff(dateRange.startDate))
        const format =
            !dateRange.endDate || !dateRange.startDate || rangeDuration.asDays() > 91
                ? 'MMM, YYYY'
                : 'DD MMM, YYYY'

        const datasets = []
        // Parse all data for each of the labels in the wanted order
        const orderedLabels =
            dataLabels && labelOrdering
                ? (Object.keys(dataLabels) as (keyof typeof dataLabels)[]).sort(labelOrdering)
                : undefined

        if (orderedLabels) {
            setGraphLegend(
                orderedLabels
                    .filter((l) => {
                        // Only include labels that have at least one non-zero value
                        return data.some((d) => {
                            const value = d[l]
                            return value !== 0
                        })
                    })
                    .map((l) => {
                        return { color: dataColours[l], label: dataLabels?.[l] || '' }
                    }),
            )
        }

        if (orderedLabels) {
            for (const key of orderedLabels) {
                const typedKey = key
                const parsedData = data.map((d) => d[typedKey])
                datasets.push({
                    data: parsedData,
                    backgroundColor: dataColours[typedKey],
                    maxBarThickness: 40,
                })
            }
        }
        const parsedData = data.map((d) => {
            return moment(d.date).format(format)
        })
        return {
            labels: parsedData,
            datasets,
        }
    }, [data, dateRange, dataLabels, labelOrdering, dataColours])

    // Create color mapping from labels to colors for tooltip
    const dataColorsMap = useMemo(() => {
        if (!dataLabels) {
            return undefined
        }
        const mapping: Record<string, string> = {}
        const orderedLabels = labelOrdering
            ? (Object.keys(dataLabels) as (keyof typeof dataLabels)[]).sort(labelOrdering)
            : (Object.keys(dataLabels) as (keyof typeof dataLabels)[])

        orderedLabels.forEach((key) => {
            const label = dataLabels[key]
            const color = dataColours[key]
            if (label && color) {
                mapping[label] = color
            }
        })
        return mapping
    }, [dataLabels, dataColours, labelOrdering])

    if (isEmpty) {
        return (
            <>
                {title && (
                    <Text variant={'h6'} sx={{ marginBottom: '20px' }}>
                        {title}
                    </Text>
                )}
                {EmptyGraph()}
            </>
        )
    }

    return (
        <>
            {data.length ? (
                <Box>
                    <LoadingWrapper loading={loading} sx={{ height: '400px', width: '100%' }}>
                        {title && (
                            <Text variant={'h6'} sx={{ marginBottom: '20px' }}>
                                {title}
                            </Text>
                        )}
                        <BarChart
                            chartHeight={chartHeight}
                            label={yAxisLabel}
                            chartData={chartData}
                            dataLabels={dataLabels}
                            labelOrdering={labelOrdering}
                            reverseTooltipOrder={reverseTooltipOrder}
                            skipZeroOnTooltip={skipZeroOnTooltip}
                            showTooltipLabels={true}
                            dataColors={dataColorsMap}
                            showTooltipColors={!!dataColorsMap}
                            getTooltipHeaderItems={getTooltipHeaderItems}
                            padLeft={data.length >= 5}
                        />
                    </LoadingWrapper>
                    {showLegend && graphLegend && <ChartLegend data={graphLegend} />}
                </Box>
            ) : (
                <Box sx={{ height: `450px` }}>
                    <NoResultsPlaceholder
                        message={
                            <Text
                                variant={`h6`}
                                sx={{
                                    textAlign: 'center',
                                    maxWidth: '250px',
                                }}
                            >
                                {t`You don't have any data for the selected time period`}
                            </Text>
                        }
                    />
                </Box>
            )}
        </>
    )
}

export default EmissionsChartSection
